import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete,
  DraftsOutlined,
  EmailOutlined,
  ErrorOutline,
  MarkEmailRead,
  MarkEmailUnread,
  Reply,
  Search,
} from "@mui/icons-material";
import { contactAdminAPI, ContactMessage } from "../../services/adminApi";

const STATUS_LABELS: Record<ContactMessage["status"], string> = {
  unread: "Unread",
  read: "Read",
  replied: "Replied",
};

const STATUS_BG: Record<ContactMessage["status"], string> = {
  unread: "#FFF1F0",
  read: "#FFFBE6",
  replied: "#F0FFF4",
};

const STATUS_TEXT: Record<ContactMessage["status"], string> = {
  unread: "#CF1322",
  read: "#874D00",
  replied: "#135200",
};

const AdminContactMessages: React.FC = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ unread: 0, read: 0, replied: 0 });

  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<ContactMessage | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await contactAdminAPI.list({
        page,
        limit: 15,
        status: statusFilter || undefined,
      });
      setMessages(res.data.data);
      setTotalPages(res.data.pagination.pages);
      setTotal(res.data.pagination.total);

      // fetch counts for all statuses (fire-and-forget)
      if (!statusFilter) {
        const [u, r, rep] = await Promise.all([
          contactAdminAPI.list({ page: 1, limit: 1, status: "unread" }),
          contactAdminAPI.list({ page: 1, limit: 1, status: "read" }),
          contactAdminAPI.list({ page: 1, limit: 1, status: "replied" }),
        ]);
        setCounts({
          unread: u.data.pagination.total,
          read: r.data.pagination.total,
          replied: rep.data.pagination.total,
        });
      }
    } catch {
      setError("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleOpen = async (msg: ContactMessage) => {
    setSelected(msg);
    setReplyText("");
    setReplyError("");
    if (msg.status === "unread") {
      try {
        const res = await contactAdminAPI.markRead(msg._id);
        setMessages((prev) => prev.map((m) => (m._id === msg._id ? res.data.data : m)));
        setSelected(res.data.data);
        setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1), read: c.read + 1 }));
      } catch {
        // non-critical
      }
    }
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) {
      setReplyError("Reply cannot be empty.");
      return;
    }
    setReplySending(true);
    setReplyError("");
    try {
      const res = await contactAdminAPI.reply(selected._id, replyText.trim());
      const updated = res.data.data;
      setMessages((prev) => prev.map((m) => (m._id === updated._id ? updated : m)));
      setSelected(updated);
      setReplyOpen(false);
      setReplyText("");
      setActionMsg(`Reply sent to ${updated.email}`);
    } catch (e: any) {
      setReplyError(e?.response?.data?.message || "Failed to send reply");
    } finally {
      setReplySending(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await contactAdminAPI.delete(deleteTarget._id);
      setActionMsg("Message deleted");
      if (selected?._id === deleteTarget._id) setSelected(null);
      setDeleteTarget(null);
      fetchMessages();
    } catch {
      setError("Failed to delete message");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredMessages = messages.filter((m) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      m.subject.toLowerCase().includes(q)
    );
  });

  const statCards = [
    { label: "Total", value: total, icon: <EmailOutlined />, color: "#1565C0", bg: "#EEF4FF" },
    { label: "Unread", value: counts.unread, icon: <MarkEmailUnread />, color: "#CF1322", bg: "#FFF1F0" },
    { label: "Read", value: counts.read, icon: <DraftsOutlined />, color: "#874D00", bg: "#FFFBE6" },
    { label: "Replied", value: counts.replied, icon: <MarkEmailRead />, color: "#135200", bg: "#F0FFF4" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#F5F7FA", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "#0A1628", mb: 0.5, letterSpacing: "-0.5px" }}>
          Contact Messages
        </Typography>
        <Typography variant="body2" sx={{ color: "#78909C" }}>
          Manage and respond to all incoming contact form submissions
        </Typography>
      </Box>

      {/* Stat cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
        {statCards.map((s) => (
          <Paper
            key={s.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2.5,
              borderRadius: "14px",
              bgcolor: "white",
              border: "1px solid rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: s.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: s.color,
                flexShrink: 0,
              }}
            >
              {s.icon}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#0A1628", lineHeight: 1.2 }}>
                {s.value}
              </Typography>
              <Typography variant="caption" sx={{ color: "#78909C", fontWeight: 500 }}>
                {s.label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Stack>

      {actionMsg && (
        <Alert severity="success" onClose={() => setActionMsg("")} sx={{ mb: 3, borderRadius: "10px" }}>
          {actionMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 3, borderRadius: "10px" }}
          icon={<ErrorOutline />}>
          {error}
        </Alert>
      )}

      {/* Filters row */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by name, email or subject…"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: 1,
            maxWidth: 380,
            "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "white" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "#9EAFC0" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(["", "unread", "read", "replied"] as const).map((val) => {
            const label = val === "" ? "All" : STATUS_LABELS[val as ContactMessage["status"]];
            const active = statusFilter === val;
            return (
              <Chip
                key={val}
                label={label}
                onClick={() => { setStatusFilter(val); setPage(1); setSelected(null); }}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  bgcolor: active ? "#1565C0" : "white",
                  color: active ? "white" : "#4A5568",
                  border: active ? "1px solid #1565C0" : "1px solid #E2E8F0",
                  "&:hover": { bgcolor: active ? "#0D47A1" : "#F0F4F8" },
                  transition: "all 0.15s",
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      {/* Main 2-col panel */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
        {/* Message list */}
        <Paper
          elevation={0}
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", md: 340, lg: 400 },
            borderRadius: "16px",
            border: "1px solid rgba(0,0,0,0.05)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            overflow: "hidden",
            bgcolor: "white",
          }}
        >
          {/* Panel header */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0A1628" }}>
              Inbox
            </Typography>
            <Typography variant="caption" sx={{ color: "#9EAFC0" }}>
              {filteredMessages.length} message{filteredMessages.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredMessages.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center", color: "#C4CDD8" }}>
              <DraftsOutlined sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2">No messages found</Typography>
            </Box>
          ) : (
            filteredMessages.map((msg, idx) => {
              const isSelected = selected?._id === msg._id;
              const isUnread = msg.status === "unread";
              return (
                <React.Fragment key={msg._id}>
                  {idx > 0 && <Divider sx={{ mx: 2, opacity: 0.5 }} />}
                  <Box
                    onClick={() => handleOpen(msg)}
                    sx={{
                      px: 2.5,
                      py: 1.8,
                      cursor: "pointer",
                      bgcolor: isSelected ? "#EEF4FF" : "white",
                      borderLeft: isSelected ? "3px solid #1565C0" : "3px solid transparent",
                      "&:hover": { bgcolor: isSelected ? "#EEF4FF" : "#F8F9FB" },
                      transition: "all 0.15s",
                      position: "relative",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Badge
                        variant="dot"
                        color="error"
                        invisible={!isUnread}
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: isSelected ? "#1565C0" : "#E8EDF5",
                            color: isSelected ? "white" : "#1565C0",
                            fontSize: "1rem",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {msg.name[0].toUpperCase()}
                        </Avatar>
                      </Badge>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isUnread ? 800 : 600,
                              color: "#0A1628",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 140,
                            }}
                          >
                            {msg.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: "0.68rem", flexShrink: 0 }}>
                            {new Date(msg.createdAt).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            })}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isUnread ? "#0A1628" : "#78909C",
                            fontWeight: isUnread ? 600 : 400,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            mb: 0.5,
                          }}
                        >
                          {msg.subject}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1,
                            py: 0.2,
                            borderRadius: "5px",
                            bgcolor: STATUS_BG[msg.status],
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              color: STATUS_TEXT[msg.status],
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                            }}
                          >
                            {STATUS_LABELS[msg.status]}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </React.Fragment>
              );
            })
          )}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Paper>

        {/* Message detail */}
        {selected ? (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: "16px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
              overflow: "hidden",
              bgcolor: "white",
              minWidth: 0,
              alignSelf: "stretch",
            }}
          >
            {/* Detail header */}
            <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: "#1565C0", width: 48, height: 48, fontSize: "1.2rem", fontWeight: 700 }}>
                    {selected.name[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: "1rem" }}>
                      {selected.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#78909C" }}>
                      {selected.email}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "8px",
                      bgcolor: STATUS_BG[selected.status],
                    }}
                  >
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: STATUS_TEXT[selected.status], textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {STATUS_LABELS[selected.status]}
                    </Typography>
                  </Box>
                  <Tooltip title="Reply by email">
                    <IconButton
                      size="small"
                      onClick={() => { setReplyOpen(true); setReplyError(""); }}
                      sx={{
                        color: "#1565C0",
                        bgcolor: "#EEF4FF",
                        border: "1px solid rgba(21,101,192,0.2)",
                        borderRadius: "9px",
                        "&:hover": { bgcolor: "#DBEAFE" },
                      }}
                    >
                      <Reply fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete message">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(selected)}
                      sx={{
                        color: "#E53E3E",
                        bgcolor: "#FFF5F5",
                        border: "1px solid rgba(229,62,62,0.2)",
                        borderRadius: "9px",
                        "&:hover": { bgcolor: "#FED7D7" },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>

            {/* Message body */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A1628", mb: 0.5 }}>
                {selected.subject}
              </Typography>
              <Typography variant="caption" sx={{ color: "#B0BEC5", display: "block", mb: 3 }}>
                Received on {new Date(selected.createdAt).toLocaleString(undefined, {
                  weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </Typography>

              <Box
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  bgcolor: "#F0F7FF",
                  border: "1px solid #DBEAFE",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.9,
                  color: "#1A2B45",
                  fontSize: "0.94rem",
                  mb: 3,
                }}
              >
                {selected.message}
              </Box>

              {selected.replyText && (
                <Box>
                  <Divider sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <MarkEmailRead sx={{ color: "#38A169", fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#38A169" }}>
                        Your Reply — {selected.repliedAt ? new Date(selected.repliedAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </Typography>
                    </Stack>
                  </Divider>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      bgcolor: "#F0FFF4",
                      border: "1px solid #C6F6D5",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.9,
                      color: "#1A2B45",
                      fontSize: "0.94rem",
                    }}
                  >
                    {selected.replyText}
                  </Box>
                </Box>
              )}

              {!selected.replyText && (
                <Box sx={{ textAlign: "center", pt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Reply />}
                    onClick={() => { setReplyOpen(true); setReplyError(""); }}
                    sx={{
                      bgcolor: "#1565C0",
                      "&:hover": { bgcolor: "#0D47A1" },
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Reply to {selected.name}
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: "16px",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              alignSelf: "stretch",
              color: "#C4CDD8",
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "20px",
                bgcolor: "#F0F4F8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <DraftsOutlined sx={{ fontSize: 40, color: "#C4CDD8" }} />
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#9EAFC0", mb: 0.5 }}>
              No message selected
            </Typography>
            <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
              Click on a message from the list to read it
            </Typography>
          </Paper>
        )}
      </Stack>

      {/* Reply dialog */}
      <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#0A1628", pb: 1 }}>
          Reply to {selected?.name}
        </DialogTitle>
        <DialogContent>
          {replyError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{replyError}</Alert>
          )}
          <Box sx={{ mb: 2, p: 1.5, borderRadius: "10px", bgcolor: "#F0F7FF", border: "1px solid #DBEAFE" }}>
            <Typography variant="caption" sx={{ color: "#4A5568" }}>
              <strong>To:</strong> {selected?.email}
            </Typography>
            <Typography variant="caption" sx={{ color: "#4A5568", display: "block" }}>
              <strong>Subject:</strong> Re: {selected?.subject}
            </Typography>
          </Box>
          <TextField
            label="Your reply"
            multiline
            rows={8}
            fullWidth
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            autoFocus
            inputProps={{ maxLength: 10000 }}
            helperText={`${replyText.length} / 10000`}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setReplyOpen(false)} sx={{ borderRadius: "9px", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReply}
            disabled={replySending}
            startIcon={replySending ? undefined : <Reply />}
            sx={{
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {replySending ? <CircularProgress size={18} color="inherit" /> : "Send Reply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#0A1628" }}>Delete Message</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#4A5568" }}>
            Permanently delete the message from{" "}
            <Typography component="span" sx={{ fontWeight: 700, color: "#0A1628" }}>
              {deleteTarget?.name}
            </Typography>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ borderRadius: "9px", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 600 }}
          >
            {deleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContactMessages;
